import { sortBy } from "lodash"
import { TreeGridTask, TreeGridTaskCellPermissions } from "../types"
import {
  getColorHtmlForColor,
  getStateIconHtml,
  makeEnumKeysFromStatusOptions,
  makeStatusEnumFromStatusOptions,
  mapColorToTranslations,
  mapStatusToTranslations,
  serializeDepedency,
  treegridDateFormat,
  treegridDateTimeFormat,
} from "../utils"
import { GanttDependency } from "../../tasks/types"
import { DateTimeService } from "../../services/date-time-service"
import { TimeZoneType } from "../../constants/timezones"
import { isTaskPlannedEndDatePastDue, isTaskPlannedStartDatePastDue } from "../../tasks/utils/date"
import { TaskViewModel } from "../../tasks/api/task"
import { TreeGridTranslations } from "../hooks/use-translations"
import { getGanttGanttHtmlRight } from "../utils/tree-grid"
import { Status } from "../../types/common"

function getActualStartDate(task: TaskViewModel, dateTimeService: DateTimeService): number | string {
  return task.actualStartDate ? dateTimeService.removeTimezoneOffset(task.actualStartDate, "UTC").getTime() : ""
}

function getActualEndDate(task: TaskViewModel, dateTimeService: DateTimeService): number | string {
  return task.actualEndDate ? dateTimeService.removeTimezoneOffset(task.actualEndDate, "UTC").getTime() : ""
}

function getActualBarStartDate(task: TaskViewModel, dateTimeService: DateTimeService): number | string {
  return getActualStartDate(task, dateTimeService)
}

function getActualBarEndDate(task: TaskViewModel, dateTimeService: DateTimeService): number | string {
  const now = new Date().toISOString()
  return task.isInProgress
    ? dateTimeService.removeTimezoneOffset(now, "UTC").getTime()
    : getActualEndDate(task, dateTimeService)
}

function getPlannedStartDate(task: TaskViewModel, dateTimeService: DateTimeService, treegridFormat: string): string {
  if (!task.plannedStartDate) return ""
  const formattedDate = dateTimeService.convertToTimeZoneAndFormat(task.plannedStartDate, treegridFormat)
  return formattedDate
}

function getPlannedEndDate(task: TaskViewModel, dateTimeService: DateTimeService, treegridFormat: string): string {
  if (!task.plannedEndDate) return ""
  const formattedDate = dateTimeService.convertToTimeZoneAndFormat(task.plannedEndDate, treegridFormat)
  return formattedDate
}

function getBasePlanStartDate(task: TaskViewModel, dateTimeService: DateTimeService): number | string {
  return task.basePlanStartDate ? dateTimeService.removeTimezoneOffset(task.basePlanStartDate, "UTC").getTime() : ""
}

function getBasePlanEndDate(task: TaskViewModel, dateTimeService: DateTimeService): number | string {
  return task.basePlanEndDate ? dateTimeService.removeTimezoneOffset(task.basePlanEndDate, "UTC").getTime() : ""
}

function getGanttDescendants(task: TaskViewModel): string {
  const inactive = task.inactiveGanttDescendants.map((dep) => serializeInactiveDependency(dep))
  const active = task.ganttDescendants.map((dep) => serializeActiveDependency(dep))
  return [...inactive, ...active].join(";")
}

function getGanttAncestors(task: TaskViewModel): string {
  const inactive = task.inactiveGanttAncestors.map((dep) => serializeInactiveDependency(dep))
  const active = task.ganttAncestors.map((dep) => serializeActiveDependency(dep))
  return [...inactive, ...active].join(";")
}

function serializeInactiveDependency(dependency: GanttDependency): string {
  return serializeDepedency(dependency, { inactive: true })
}

function serializeActiveDependency(dependency: GanttDependency): string {
  return serializeDepedency(dependency)
}

function getPlannedStartDateClass(task: TaskViewModel, dateTimeService: DateTimeService): string {
  const isPastDue = isTaskPlannedStartDatePastDue({ task, dateTimeService })
  return isPastDue ? "redText" : ""
}

function getPlannedEndDateClass(task: TaskViewModel, dateTimeService: DateTimeService): string {
  const isPastDue = isTaskPlannedEndDatePastDue({ task, dateTimeService })
  return isPastDue ? "redText" : ""
}

function getStatusDescriptionTip(task: TaskViewModel, dateTimeService: DateTimeService, dateFormat: string): string {
  const sortedUpdates = sortBy(task.statusDescriptionUpdates, "updatedAt")
  const latestUpdate = sortedUpdates[sortedUpdates.length - 1]
  let tip = ""
  if (latestUpdate) {
    const formatStr = `${dateFormat} HH:mm`
    const { updatedBy, updatedAt } = latestUpdate
    const offsetDate = dateTimeService.removeTimezoneOffset(updatedAt)
    tip += updatedBy
    tip += "&nbsp;&nbsp;&bull;&nbsp;&nbsp;"
    tip += dateTimeService.format(offsetDate, formatStr)
  }
  return tip
}

function getTreeGridTaskCellPermissions(task: TaskViewModel): TreeGridTaskCellPermissions {
  return {
    CanDelete: task.canDelete ? 1 : 0,
    CanDrag: task.canUpdateDetails ? 1 : 0,
    titleCanEdit: task.canUpdateDetails ? 1 : 0,
    descriptionCanEdit: task.canUpdateDetails ? 1 : 0,
    ganttBarColorCanEdit: task.canUpdateDetails ? 1 : 0,
    taskNumberCanEdit: task.canUpdateDetails ? 1 : 0,
    plannedStartDateCanEdit: task.canUpdatePlan ? 1 : 0,
    plannedEndDateCanEdit: task.canUpdatePlan ? 1 : 0,
    durationCanEdit: task.canUpdatePlan ? 1 : 0,
    daysLeftCanEdit: task.canUpdatePlan && task.canUpdateStatus && !task.isCompleted ? 1 : 0,
    statusCanEdit: task.canUpdateStatus ? 1 : 0,
    stateCanEdit: task.canUpdateStatus ? 1 : 0,
    statusDescriptionCanEdit: task.canUpdateStatus ? 1 : 0,
    actualEndDateCanEdit: task.canUpdateStatus ? 1 : 0,
    actualStartDateCanEdit: task.canUpdateStatus ? 1 : 0,
    managersCanEdit: task.canUpdateManagers ? 1 : 0,
    suppliersCanEdit: task.canUpdateSuppliers ? 1 : 0,
    workspacesCanEdit: task.canUpdateWorkspaces ? 1 : 0,
    participantsCanEdit: task.canUpdateParticipants ? 1 : 0,
    GanttCanEdit: task.canUpdatePlan ? 0 : 1, // TODO: Need to find out why Gantt works this way
    ganttAncestorsCanEdit: 0, // DON'T ALLOW EDITING OF GANTT ANCESTORS COLUMN
    completionPercentageCanEdit: task.canUpdateStatus && !task.hasActiveChildren ? 1 : 0,
  }
}

export const makeTreeGridTask = ({
  task,
  dateFormat,
  timeZone,
  translations,
}: {
  task: TaskViewModel
  dateFormat: string
  timeZone: TimeZoneType
  translations: TreeGridTranslations
}): TreeGridTask => {
  const { id, title, description, statusDescription, projectId, enableTimeComponent } = task
  const treegridFormat = enableTimeComponent ? treegridDateTimeFormat : treegridDateFormat
  const dateTimeService = new DateTimeService({ dateFormat, timeZone, enableTimeComponent })
  const taskNumber = task.customTaskNumber || task.taskNumber
  const participants = task.participants.map((p) => p.id).join(";")
  const managers = task.managers.map((m) => m.id).join(";")
  const suppliers = task.suppliers.map((s) => s.id).join(";")
  const workspaces = task.workspaces.map((w) => w.id).join(";")
  const plannedStartDate = getPlannedStartDate(task, dateTimeService, treegridFormat)
  const plannedEndDate = getPlannedEndDate(task, dateTimeService, treegridFormat)
  const basePlanStartDate = getBasePlanStartDate(task, dateTimeService)
  const basePlanEndDate = getBasePlanEndDate(task, dateTimeService)
  const actualStartDate = getActualStartDate(task, dateTimeService)
  const actualEndDate = getActualEndDate(task, dateTimeService)
  const actualBarStartDate = getActualBarStartDate(task, dateTimeService)
  const actualBarEndDate = getActualBarEndDate(task, dateTimeService)
  const ganttGanttHtmlRight = getGanttGanttHtmlRight({
    title: task.title,
    startDate: task.plannedStartDate,
    endDate: task.plannedEndDate,
    dateTimeService,
    showTime: enableTimeComponent,
  })

  const cellPermissions = getTreeGridTaskCellPermissions(task)
  const ganttDescendants = getGanttDescendants(task)
  const ganttAncestors = getGanttAncestors(task)
  const state = getStateIconHtml(task.state)
  const plannedStartDateClass = getPlannedStartDateClass(task, dateTimeService)
  const plannedEndDateClass = getPlannedEndDateClass(task, dateTimeService)
  const statusDescriptionTip = getStatusDescriptionTip(task, dateTimeService, dateFormat)
  const dependencies = JSON.stringify(task.ganttAncestors.length ? task.ganttAncestors : [])
  const statusTranslations = mapStatusToTranslations(translations)
  const status = statusTranslations[task.customStatus as Status] || task.customStatus
  const statusEnum = makeStatusEnumFromStatusOptions(task.statusOptions, translations)
  const statusEnumKeys = makeEnumKeysFromStatusOptions(task.statusOptions)
  const translatedColor = mapColorToTranslations(translations)[task.ganttBarColor]
  const ganttBarColor = getColorHtmlForColor({ color: task.ganttBarColor, translatedColor })
  const GanttGanttClass = task.ganttBarColor === "Default" ? undefined : task.ganttBarColor

  const linksHtml = task.links.map((link) => link.anchorTag)
  let filesHtml: string[] = []
  if (task.canViewTaskAttachments && task.files.length > 0) {
    filesHtml = task.files.map(
      (file) => `<a href="#" data-file-id="${file.id}" onclick="return false;">${file.filename}</a>`
    )
  }
  const attachmentsHtml = [...linksHtml, ...filesHtml].join(", ")

  return {
    open: "/external-link-icon.svg",
    id,
    title,
    description,
    status,
    state,
    statusDescription,
    taskNumber,
    participants,
    managers,
    suppliers,
    workspaces,
    duration: task.duration,
    durationAlign: "center",
    daysLeft: task.daysLeft,
    daysLeftAlign: "center",
    plannedStartDate,
    plannedEndDate,
    actualStartDate,
    actualEndDate,
    actualBarStartDate,
    actualBarEndDate,
    ganttDescendants,
    ganttAncestors,
    ganttGanttHtmlRight,
    ganttBarColor,
    ganttBarColorFilterValue: translatedColor,
    GanttGanttClass,
    plannedStartDateClass,
    plannedEndDateClass,
    statusDescriptionTip,
    parentTaskId: task.parentTaskId || null,
    projectId,
    order: task.order,
    canCreateTasks: task.canCreate,
    dependencies,
    basePlanEndDate,
    basePlanStartDate,
    linksAndAttachments: attachmentsHtml,
    statusEnum,
    statusEnumKeys,
    completionPercentage: task.completionPercentage,
    completionPercentageHtmlPostfix: "%",
    ...cellPermissions,
  }
}
